import axios from "axios";
import store from "@/store"; // Adjust the path to your Vuex store

const baseClient = axios.create({
    baseURL: process.env.VUE_APP_BASE_API_URL,
});

// Returns true when the token is missing, malformed, or past its `exp` claim.
// The backend rejects an expired/invalid token as anonymous, which Spring Security
// then denies with 403 — the same status a valid but under-privileged user gets on
// a forbidden resource. We can't tell those apart from the status alone, so we decode
// the token locally: an expired/invalid token means the session is over (log out),
// whereas a still-valid token means a genuine permission denial (leave the user in).
function isTokenExpiredOrMissing(token) {
    if (!token) return true;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // No `exp` claim: we can't prove it's expired, so treat it as still valid.
        if (!payload.exp) return false;
        return payload.exp * 1000 <= Date.now();
    } catch (e) {
        // Undecodable token — treat as invalid.
        return true;
    }
}

baseClient.interceptors.request.use(
    config => {
        // Pull token from Vuex, fallback to localStorage in case of page refresh
        const token = store.state.auth.token || localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

baseClient.interceptors.response.use(
    response => response,
    error => {
        if (error.response) {
            const status = error.response.status;
            const token = store.state.auth.token || localStorage.getItem('token');
            // 401: session expired. 403 with a missing/expired token: the backend
            // treated our token as anonymous (e.g. it expired, or was cleared by the
            // visibility-change handler before the request completed). Either way the
            // session is over, so log out and send the user back to login. A 403 with a
            // still-valid token is a genuine permission denial — leave the session intact.
            if (status === 401 || (status === 403 && isTokenExpiredOrMissing(token))) {
                store.dispatch('auth/logout');
                window.location.href = '/';
            }
        }
        return Promise.reject(error);
    }
);

export default baseClient;